import React, { useRef } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'

import { BEIGE_200, BEIGE_600, GRAY_500, GRAY_700 } from 'constants/styling/theme'
import { BeforeAfterSlider } from 'components/common/BeforeAfterSlider'
import type { ProductPreview as ProductPreviewDTO } from 'models/purchaseFlowRevamp'

interface Props {
  /** Resolved product preview descriptor. May be missing — fallback tile renders. */
  preview?: ProductPreviewDTO
  /** Accessible alt text — typically the product display name. */
  alt: string
  /** Visual height of the preview. Accepts any CSS length. */
  height?: string | number
  /** Visual width. Defaults to filling the card; the catalogue's thumbnail cards
   *  pass a fixed square instead. */
  width?: string | number
  /** Corner radius. Defaults to the revamp's 0.8rem; the catalogue's thumbnail
   *  tile is `--radius-lg` (1rem) in the design. */
  borderRadius?: string
  /**
   * When true, the video preview plays on hover (mouseenter) and pauses on
   * mouseleave. Has no effect on image or before/after previews.
   *
   * Clicks always propagate regardless of this flag — parent card / drawer
   * handlers are never suppressed here.
   */
  interactive?: boolean
}

/**
 * Renders the correct preview variant for a product card:
 *   - `image`        → `<img>` cover
 *   - `video`        → video with hover-to-play; subtle corner icon indicator; click always bubbles
 *   - `before_after` → compact `<BeforeAfterSlider>` thumbnail; the split follows the mouse when `interactive`
 *   - `none`         → fallback tile with a generic icon
 *
 * When `interactive` is true, hover-to-play is enabled for video previews and the before/after split
 * follows the hovering mouse.
 * Clicks always propagate — parent handlers (card-open, etc.) fire normally.
 * Only the slider handle stops propagation (scoped inside BeforeAfterSlider).
 */
export const ProductPreview: React.FC<Props> = ({
  preview,
  alt,
  height = '18rem',
  width = '100%',
  borderRadius = '0.8rem',
  interactive = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  const sharedBoxSx = {
    height,
    width,
    flexShrink: 0,
    borderRadius,
    overflow: 'hidden',
    backgroundColor: BEIGE_200,
    position: 'relative' as const,
  }

  if (preview?.kind === 'image' && preview.imageUrl) {
    return (
      <Box
        component="img"
        src={preview.imageUrl}
        alt={alt}
        sx={{
          ...sharedBoxSx,
          display: 'block',
          objectFit: 'cover',
        }}
      />
    )
  }

  if (preview?.kind === 'video') {
    const handleMouseEnter = () => {
      if (!interactive) return
      videoRef.current?.play()
    }

    const handleMouseLeave = () => {
      if (!interactive) return
      videoRef.current?.pause()
    }

    return (
      <Box
        sx={sharedBoxSx}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >

        {preview.videoUrl &&
          <Box
            ref={videoRef}
            component="video"
            src={preview.videoUrl}
            muted
            loop
            playsInline
            preload="metadata"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        }

        {/* Subtle video indicator — top-right corner, pointer-events:none so
            clicks pass through to the parent card's onClick handler. */}
        <Box
          sx={{
            position: 'absolute',
            top: '0.8rem',
            right: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '2.8rem',
            height: '2.8rem',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            boxShadow: '0px 1px 4px rgba(0,0,0,0.18)',
            color: GRAY_700,
            pointerEvents: 'none',
          }}
        >
          <VideocamOutlinedIcon sx={{ fontSize: '1.8rem' }} />
        </Box>

      </Box>
    )
  }

  if (preview?.kind === 'before_after' && preview.beforeImageUrl && preview.afterImageUrl) {
    return (
      <Box sx={{ ...sharedBoxSx, backgroundColor: 'transparent' }}>
        <BeforeAfterSlider
          beforeImage={preview.beforeImageUrl}
          afterImage={preview.afterImageUrl}
          beforeAlt={alt}
          afterAlt={alt}
          variant="thumbnail"
          height={height}
          followPointer={interactive}
          handleOnlyDrag
        />
      </Box>
    )
  }

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap={0.4}
      sx={{ ...sharedBoxSx, border: `1px dashed ${BEIGE_600}` }}
    >

      <ImageOutlinedIcon sx={{ fontSize: '3.6rem', color: GRAY_500 }} />

      <Typography variant="text-xs" color={GRAY_700}>
        {alt}
      </Typography>

    </Stack>
  )
}
